<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getWhatsappConversationPhone,
  useWhatsappCall,
} from 'dashboard/composables/useWhatsappCall';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const dialogRef = ref(null);
const isOpen = ref(false);
const selectedConnectionId = ref(null);
const reactionOptions = ['👍', '❤️', '😂'];

const {
  call,
  status,
  conversation: activeConversation,
  connections,
  connection,
  connectionId,
  isIncoming,
  isStarting,
  isPerformingAction,
  isLoadingConnections,
  isRefreshingConnection,
  error,
  mediaError,
  callLinkResult,
  loadConnections,
  startCall,
  createCallLink,
  previewCallLink,
  joinCallLink,
  receiveIncomingCall,
  answerCall,
  rejectCall,
  endCall,
  setMuted,
  startVideo,
  acceptVideo,
  stopVideo,
  setVideoEnabled,
  sendReaction,
  setHandRaised,
  startScreenShare,
  stopScreenShare,
  addParticipant,
  ringParticipant,
  setApprovalRequired,
  admitWaitingUser,
  denyWaitingUser,
  setVideoCanvas,
  refreshConnection,
  updateConnection,
  cleanup,
} = useWhatsappCall();
const participantTarget = ref('');
const groupId = ref('');
const groupTargetsInput = ref('');
const callLinkInput = ref('');

const displayConversation = computed(
  () => activeConversation.value || props.conversation
);
const phone = computed(
  () =>
    getWhatsappConversationPhone(displayConversation.value) ||
    call.value?.remote_identifier ||
    ''
);
const contactName = computed(() => {
  const sender = displayConversation.value?.meta?.sender || {};
  return sender.name || sender.available_name || phone.value;
});
const hasCall = computed(() => Boolean(call.value?.id));
const isRinging = computed(() => status.value === 'ringing');
const isConnected = computed(() => status.value === 'connected');
const isMuted = computed(() => call.value?.muted === true);
const isRemoteMuted = computed(() => call.value?.remote_muted === true);
const isVideo = computed(() => call.value?.video === true);
const isSendingVideo = computed(() => call.value?.sending_video === true);
const isReceivingVideo = computed(() => call.value?.receiving_video === true);
const needsVideoAcceptance = computed(() =>
  [3, 11].includes(Number(call.value?.video_state))
);
const groupParticipants = computed(() => call.value?.group?.participants || []);
const waitingRoom = computed(() => call.value?.waiting_room || null);
const screenShares = computed(() => call.value?.screen_shares || []);
const isScreenSharing = computed(() =>
  screenShares.value.some(share => share.active === true)
);
const lastReaction = computed(() => {
  const event = call.value?.last_call_feature_event;
  return event?.event === 'call.reaction' && event?.data?.removed !== true
    ? event.data
    : null;
});
const isTerminal = computed(() =>
  ['rejected', 'ended', 'failed', 'missed'].includes(status.value)
);
const selectedConnection = computed(() =>
  connections.value.find(
    availableConnection =>
      String(availableConnection.id) === String(selectedConnectionId.value)
  )
);
const selectedConnectionReady = computed(() =>
  connection.value?.id === selectedConnection.value?.id
    ? connection.value?.call_gateway_connected === true
    : selectedConnection.value?.call_gateway_connected === true
);
const canStartCall = computed(
  () =>
    Boolean(selectedConnectionId.value) &&
    selectedConnectionReady.value &&
    !isStarting.value
);
const groupTargets = computed(() => [
  ...new Set(
    groupTargetsInput.value
      .split(/[\n,;]+/)
      .map(value => value.trim())
      .filter(Boolean)
  ),
]);
const hasGroupId = computed(() => Boolean(groupId.value.trim()));
const hasGroupTargets = computed(() => groupTargets.value.length > 0);
const canStartGroupCall = computed(
  () => canStartCall.value && hasGroupId.value !== hasGroupTargets.value
);
const canUseCallLink = computed(
  () => canStartCall.value && Boolean(callLinkInput.value.trim())
);
const callLinkSummary = computed(() => {
  if (callLinkResult.value?.url) return callLinkResult.value.url;
  if (callLinkResult.value?.creator) {
    return t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.PREVIEW_RESULT', {
      creator: callLinkResult.value.creator,
      approval: callLinkResult.value.approval_required
        ? t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.APPROVAL_REQUIRED')
        : t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.APPROVAL_OPEN'),
    });
  }
  return '';
});
const statusLabel = computed(() => {
  const labels = {
    idle: t('CONVERSATION.WHATSAPP_CALL.STATUS.STARTING'),
    ringing: t('CONVERSATION.WHATSAPP_CALL.STATUS.RINGING'),
    connecting: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTING'),
    connected: t('CONVERSATION.WHATSAPP_CALL.STATUS.CONNECTED'),
    rejected: t('CONVERSATION.WHATSAPP_CALL.STATUS.REJECTED'),
    ended: t('CONVERSATION.WHATSAPP_CALL.STATUS.ENDED'),
    failed: t('CONVERSATION.WHATSAPP_CALL.STATUS.FAILED'),
    missed: t('CONVERSATION.WHATSAPP_CALL.STATUS.MISSED'),
  };

  return labels[status.value] || labels.idle;
});
const connectionStatusLabel = computed(() => {
  const labels = {
    open: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTED'),
    connected: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTED'),
    ready: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTED'),
    connecting: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTING'),
    pending: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTING'),
    close: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.DISCONNECTED'),
    closed: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.DISCONNECTED'),
    offline: t('CONVERSATION.WHATSAPP_CALL.CONNECTION.DISCONNECTED'),
  };
  const gatewayStatus = String(
    connection.value?.call_gateway_status || connection.value?.status || ''
  ).toLowerCase();
  if (connection.value?.call_gateway_connected === true) {
    return t('CONVERSATION.WHATSAPP_CALL.CONNECTION.CONNECTED');
  }
  if (
    connection.value?.qr_code ||
    ['awaiting_qr', 'connecting', 'pending'].includes(gatewayStatus)
  ) {
    return t('CONVERSATION.WHATSAPP_CALL.CONNECTION.PAIRING');
  }
  return (
    labels[gatewayStatus] ||
    t('CONVERSATION.WHATSAPP_CALL.CONNECTION.UNAVAILABLE')
  );
});
const qrCode = computed(() => connection.value?.qr_code || '');
const callError = computed(() => error.value || mediaError.value);
const callErrorMessage = computed(() => {
  if (
    [
      'WhatsApp Web Calls session is not connected',
      'WhatsApp session is not connected',
    ].includes(callError.value)
  ) {
    return t('CONVERSATION.WHATSAPP_CALL.CONNECTION.UNAVAILABLE');
  }

  return callError.value;
});

const connectionLabel = availableConnection =>
  availableConnection.profile_name ||
  availableConnection.phone_number ||
  t('CONVERSATION.WHATSAPP_CALL.CONNECTION.OPTION', {
    id: availableConnection.id,
  });

const selectConnection = async connectionToUse => {
  selectedConnectionId.value = connectionToUse;
  await updateConnection(connectionToUse);
};

const open = async () => {
  if (!props.conversation?.id || isStarting.value) return false;

  isOpen.value = true;
  selectedConnectionId.value = null;
  groupId.value = '';
  groupTargetsInput.value = '';
  callLinkInput.value = '';
  dialogRef.value?.open();
  const availableConnections = await loadConnections(props.conversation);
  if (availableConnections.length === 1) {
    await selectConnection(availableConnections[0].id);
  }
  return true;
};

const openIncoming = incomingPayload => {
  if (
    isOpen.value &&
    String(call.value?.id || '') === String(incomingPayload?.call?.id || '')
  ) {
    return true;
  }

  const received = receiveIncomingCall(incomingPayload);
  if (!received || isTerminal.value || isOpen.value) return received;

  isOpen.value = true;
  dialogRef.value?.open();
  return true;
};

const handleClose = async () => {
  if (hasCall.value && !isTerminal.value && !isPerformingAction.value) {
    await endCall();
  }
  isOpen.value = false;
  selectedConnectionId.value = null;
  cleanup();
};

const startSelectedCall = async () => {
  await startCall(props.conversation, selectedConnectionId.value);
};

const startSelectedVideoCall = async () => {
  await startCall(props.conversation, selectedConnectionId.value, {
    video: true,
  });
};

const startSelectedGroupCall = async video => {
  await startCall(props.conversation, selectedConnectionId.value, {
    video,
    groupId: groupId.value.trim() || undefined,
    targets: hasGroupTargets.value ? groupTargets.value : undefined,
  });
};

const createSelectedCallLink = async video => {
  const result = await createCallLink(selectedConnectionId.value, { video });
  if (result?.url) callLinkInput.value = result.url;
};

const previewSelectedCallLink = async () => {
  await previewCallLink(selectedConnectionId.value, callLinkInput.value);
};

const joinSelectedCallLink = async video => {
  await joinCallLink(
    props.conversation,
    selectedConnectionId.value,
    callLinkInput.value,
    { video }
  );
};

const handleAnswer = async () => {
  await answerCall();
};

const handleReject = async () => {
  if (await rejectCall()) dialogRef.value?.close();
};

const handleHangup = async () => {
  if (await endCall()) dialogRef.value?.close();
};

const toggleMute = async () => {
  await setMuted(!isMuted.value);
};

const toggleCamera = async () => {
  if (!isVideo.value) {
    await startVideo();
    return;
  }
  await setVideoEnabled(!isSendingVideo.value);
};

const handleStopVideo = async () => {
  await stopVideo();
};

const toggleScreenShare = async () => {
  if (isScreenSharing.value) await stopScreenShare();
  else await startScreenShare();
};

const inviteParticipant = async () => {
  const target = participantTarget.value.trim();
  if (!target) return;
  if (await addParticipant(target)) participantTarget.value = '';
};

const attachVideoCanvas = canvas => {
  setVideoCanvas(canvas);
};

const refreshGateway = async () => {
  if (!connectionId.value) return;

  const updatedConnection = await updateConnection(connectionId.value, {
    refresh: true,
  });
  if (!updatedConnection) await refreshConnection(connectionId.value);
};

defineExpose({ open, openIncoming });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('CONVERSATION.WHATSAPP_CALL.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="sm"
    @close="handleClose"
  >
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <span
          class="flex items-center justify-center w-10 h-10 rounded-full bg-n-teal-9/10 text-n-teal-11"
          :class="{ 'animate-pulse': isRinging }"
        >
          <i class="text-xl i-ph-phone-call-bold" />
        </span>
        <div class="min-w-0">
          <p class="mb-0 text-sm font-medium truncate text-n-slate-12">
            {{ contactName }}
          </p>
          <p v-if="isIncoming" class="mb-0 text-xs text-n-teal-11">
            {{ $t('CONVERSATION.WHATSAPP_CALL.INCOMING') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">{{ phone }}</p>
        </div>
      </div>

      <div
        v-if="hasCall || isStarting"
        class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-2 text-sm text-n-slate-11"
        role="status"
      >
        <span
          class="w-2 h-2 rounded-full"
          :class="isConnected ? 'bg-n-teal-9' : 'bg-n-amber-9'"
        />
        {{
          isStarting ? $t('CONVERSATION.WHATSAPP_CALL.STARTING') : statusLabel
        }}
      </div>

      <div
        v-if="!hasCall && !isIncoming"
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.WHATSAPP_CALL.CONNECTION.SELECT_TITLE') }}
        </p>
        <p v-if="isLoadingConnections" class="mb-0 text-sm text-n-slate-11">
          {{ $t('CONVERSATION.WHATSAPP_CALL.CONNECTION.LOADING') }}
        </p>
        <p v-else-if="!connections.length" class="mb-0 text-sm text-n-ruby-11">
          {{ $t('CONVERSATION.WHATSAPP_CALL.CONNECTION.NONE_AVAILABLE') }}
        </p>
        <label
          v-for="availableConnection in connections"
          :key="availableConnection.id"
          class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-n-alpha-2"
        >
          <input
            v-model="selectedConnectionId"
            type="radio"
            name="whatsapp-call-connection"
            :value="availableConnection.id"
            @change="selectConnection(availableConnection.id)"
          />
          <span class="flex flex-col min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ connectionLabel(availableConnection) }}
            </span>
            <span
              v-if="availableConnection.phone_number"
              class="text-xs text-n-slate-11"
            >
              {{ availableConnection.phone_number }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                $t('CONVERSATION.WHATSAPP_CALL.CONNECTION.OPTION', {
                  id: availableConnection.id,
                })
              }}
            </span>
          </span>
        </label>
      </div>

      <div
        v-if="connection || connectionId"
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center justify-between gap-3">
          <div>
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ $t('CONVERSATION.WHATSAPP_CALL.CONNECTION.TITLE') }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ connectionStatusLabel }}
            </p>
          </div>
          <Button
            v-if="connectionId"
            data-testid="whatsapp-call-refresh-qr"
            ghost
            slate
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CONNECTION.REFRESH')"
            :is-loading="isRefreshingConnection"
            :disabled="isRefreshingConnection"
            @click="refreshGateway"
          />
        </div>
        <img
          v-if="qrCode"
          :key="qrCode"
          :src="qrCode"
          :alt="$t('CONVERSATION.WHATSAPP_CALL.CONNECTION.QR_ALT')"
          class="self-center w-44 h-44 p-2 bg-white rounded-lg"
        />
      </div>

      <div
        v-if="!hasCall && !isIncoming && selectedConnectionReady"
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.WHATSAPP_CALL.GROUP.START_TITLE') }}
        </p>
        <input
          v-model="groupId"
          type="text"
          class="w-full px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-1"
          :placeholder="
            $t('CONVERSATION.WHATSAPP_CALL.GROUP.GROUP_ID_PLACEHOLDER')
          "
        />
        <textarea
          v-model="groupTargetsInput"
          rows="2"
          class="w-full px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-1"
          :placeholder="
            $t('CONVERSATION.WHATSAPP_CALL.GROUP.TARGETS_PLACEHOLDER')
          "
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ $t('CONVERSATION.WHATSAPP_CALL.GROUP.START_HELP') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            color="teal"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.START_AUDIO')"
            :disabled="!canStartGroupCall"
            @click="startSelectedGroupCall(false)"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.START_VIDEO')"
            :disabled="!canStartGroupCall"
            @click="startSelectedGroupCall(true)"
          />
        </div>
      </div>

      <div
        v-if="!hasCall && !isIncoming && selectedConnectionReady"
        class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.TITLE') }}
        </p>
        <input
          v-model="callLinkInput"
          type="text"
          class="w-full px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-1"
          :placeholder="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.PLACEHOLDER')"
        />
        <div class="flex flex-wrap gap-2">
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.CREATE_AUDIO')"
            :disabled="!canStartCall"
            @click="createSelectedCallLink(false)"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.CREATE_VIDEO')"
            :disabled="!canStartCall"
            @click="createSelectedCallLink(true)"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.PREVIEW')"
            :disabled="!canUseCallLink"
            @click="previewSelectedCallLink"
          />
          <Button
            color="teal"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.JOIN_AUDIO')"
            :disabled="!canUseCallLink"
            @click="joinSelectedCallLink(false)"
          />
          <Button
            color="teal"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.CALL_LINK.JOIN_VIDEO')"
            :disabled="!canUseCallLink"
            @click="joinSelectedCallLink(true)"
          />
        </div>
        <p
          v-if="callLinkSummary"
          class="mb-0 text-xs break-all text-n-slate-11"
        >
          {{ callLinkSummary }}
        </p>
      </div>

      <p
        v-if="callErrorMessage"
        role="alert"
        class="mb-0 text-sm text-n-ruby-11"
      >
        {{ callErrorMessage }}
      </p>
      <p v-if="isRemoteMuted" class="mb-0 text-sm text-n-slate-11">
        {{ $t('CONVERSATION.WHATSAPP_CALL.REMOTE_MUTED') }}
      </p>
      <div
        v-if="isConnected && (isVideo || isReceivingVideo)"
        class="flex flex-col gap-2"
      >
        <canvas
          :ref="attachVideoCanvas"
          class="w-full max-h-64 rounded-lg bg-n-slate-12"
          :aria-label="$t('CONVERSATION.WHATSAPP_CALL.VIDEO.REMOTE')"
        />
        <div class="flex flex-wrap gap-2">
          <Button
            color="slate"
            sm
            :label="
              isSendingVideo
                ? $t('CONVERSATION.WHATSAPP_CALL.VIDEO.STOP_CAMERA')
                : $t('CONVERSATION.WHATSAPP_CALL.VIDEO.START_CAMERA')
            "
            :is-loading="isPerformingAction"
            :disabled="isPerformingAction"
            @click="toggleCamera"
          />
          <Button
            v-if="needsVideoAcceptance"
            color="teal"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.VIDEO.ACCEPT')"
            :is-loading="isPerformingAction"
            :disabled="isPerformingAction"
            @click="acceptVideo"
          />
          <Button
            v-if="isSendingVideo"
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.VIDEO.STOP_VIDEO')"
            :is-loading="isPerformingAction"
            :disabled="isPerformingAction"
            @click="handleStopVideo"
          />
          <Button
            color="slate"
            sm
            :label="
              isScreenSharing
                ? $t('CONVERSATION.WHATSAPP_CALL.VIDEO.STOP_SCREEN_SHARE')
                : $t('CONVERSATION.WHATSAPP_CALL.VIDEO.START_SCREEN_SHARE')
            "
            :is-loading="isPerformingAction"
            :disabled="isPerformingAction"
            @click="toggleScreenShare"
          />
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            v-for="reaction in reactionOptions"
            :key="reaction"
            color="slate"
            sm
            :label="reaction"
            :disabled="isPerformingAction"
            @click="sendReaction(reaction)"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.VIDEO.RAISE_HAND')"
            :disabled="isPerformingAction"
            @click="setHandRaised(true)"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.VIDEO.LOWER_HAND')"
            :disabled="isPerformingAction"
            @click="setHandRaised(false)"
          />
        </div>
        <p v-if="lastReaction" class="mb-0 text-sm text-n-slate-11">
          {{
            $t('CONVERSATION.WHATSAPP_CALL.VIDEO.REACTION_FROM', {
              sender: lastReaction.sender,
              emoji: lastReaction.emoji,
            })
          }}
        </p>
      </div>
      <div
        v-if="groupParticipants.length"
        class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-solid-1"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.WHATSAPP_CALL.GROUP.PARTICIPANTS') }}
        </p>
        <div
          v-for="participant in groupParticipants"
          :key="participant.jid"
          class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
        >
          <span class="truncate">{{ participant.jid }}</span>
          <Button
            v-if="participant.can_ring"
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.RING')"
            :disabled="isPerformingAction"
            @click="ringParticipant(participant.jid)"
          />
        </div>
        <div class="flex gap-2">
          <input
            v-model="participantTarget"
            type="text"
            class="flex-1 min-w-0 px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-1"
            :placeholder="
              $t('CONVERSATION.WHATSAPP_CALL.GROUP.PARTICIPANT_PLACEHOLDER')
            "
            @keyup.enter="inviteParticipant"
          />
          <Button
            color="slate"
            sm
            :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.ADD')"
            :disabled="isPerformingAction || !participantTarget.trim()"
            @click="inviteParticipant"
          />
        </div>
        <div
          v-if="waitingRoom?.is_admin && waitingRoom.users?.length"
          class="flex flex-col gap-2"
        >
          <p class="mb-0 text-xs font-medium text-n-slate-12">
            {{ $t('CONVERSATION.WHATSAPP_CALL.GROUP.WAITING_ROOM') }}
          </p>
          <div
            v-for="user in waitingRoom.users"
            :key="user.jid"
            class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
          >
            <span class="truncate">{{ user.jid }}</span>
            <span class="flex gap-1">
              <Button
                color="teal"
                sm
                :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.ADMIT')"
                @click="admitWaitingUser(user.jid)"
              />
              <Button
                color="ruby"
                sm
                :label="$t('CONVERSATION.WHATSAPP_CALL.GROUP.DENY')"
                @click="denyWaitingUser(user.jid)"
              />
            </span>
          </div>
        </div>
        <Button
          v-if="waitingRoom?.is_admin"
          color="slate"
          sm
          :label="
            waitingRoom.enabled
              ? $t('CONVERSATION.WHATSAPP_CALL.GROUP.DISABLE_APPROVAL')
              : $t('CONVERSATION.WHATSAPP_CALL.GROUP.ENABLE_APPROVAL')
          "
          :disabled="isPerformingAction"
          @click="setApprovalRequired(!waitingRoom.enabled)"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex w-full gap-2">
        <Button
          v-if="!hasCall && !isIncoming"
          color="teal"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.START_CALL')"
          :is-loading="isStarting"
          :disabled="!canStartCall"
          @click="startSelectedCall"
        />
        <Button
          v-if="!hasCall && !isIncoming"
          color="slate"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.START_VIDEO_CALL')"
          :is-loading="isStarting"
          :disabled="!canStartCall"
          @click="startSelectedVideoCall"
        />
        <Button
          v-if="isRinging"
          variant="faded"
          color="ruby"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.REJECT')"
          :is-loading="isPerformingAction"
          :disabled="isPerformingAction"
          @click="handleReject"
        />
        <Button
          v-if="isRinging"
          color="teal"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.ANSWER')"
          :is-loading="isPerformingAction"
          :disabled="isPerformingAction"
          @click="handleAnswer"
        />
        <Button
          v-if="isConnected"
          color="slate"
          class="flex-1"
          :label="
            isMuted
              ? $t('CONVERSATION.WHATSAPP_CALL.UNMUTE')
              : $t('CONVERSATION.WHATSAPP_CALL.MUTE')
          "
          :is-loading="isPerformingAction"
          :disabled="isPerformingAction"
          @click="toggleMute"
        />
        <Button
          v-if="isConnected || isRinging"
          color="ruby"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.HANG_UP')"
          :is-loading="isPerformingAction"
          :disabled="isPerformingAction"
          @click="handleHangup"
        />
        <Button
          v-if="isTerminal || callError"
          color="slate"
          class="flex-1"
          :label="$t('CONVERSATION.WHATSAPP_CALL.CLOSE')"
          @click="dialogRef?.close()"
        />
      </div>
    </template>
  </Dialog>
</template>
